import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "../lib/cn";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      // Base + pointer hover (background only, no border swap). Swapping the
      // border color on hover/click read as a stray focus ring in the recorded
      // bug; a background-only hover keeps the trigger feeling interactive
      // without the outline flash.
      "flex h-9 w-full items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2 type-body-s-regular ring-offset-white placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer [&>span]:line-clamp-1 dark:ring-offset-neutral-800 dark:placeholder:text-neutral-400",
      "hover:bg-[var(--color-surface-hover)]",
      // See DESIGN.md, "Dropdown trigger focus (no persistent outline while
      // open)". Keep the standard focus-visible border for keyboard users when
      // closed, but suppress it once Radix opens the menu: the popover panel
      // itself is the focus indicator, and otherwise the border briefly flashes
      // on click because the trigger receives focus right before content opens.
      "focus:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0",
      "data-[state=open]:focus-visible:border data-[state=open]:focus-visible:border-[var(--color-border)] data-[state=open]:dark:focus-visible:border-[var(--color-border)]",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="w-4 h-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUp className="w-4 h-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDown className="w-4 h-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-100 max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-[#0c0c0d14] bg-white text-neutral-900 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.12)] cursor-pointer dark:border-white/12 dark:bg-[#2A2C2C] dark:text-white dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.2)]",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1 cursor-pointer",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "py-1.5 pl-8 pr-2 type-body-s-regular font-semibold",
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    description?: React.ReactNode;
  }
>(({ className, children, description, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none rounded-md py-1.5 pl-8 pr-2 type-body-s-regular outline-hidden text-neutral-900",
      description ? "items-start py-2" : "items-center",
      "focus:bg-neutral-100 dark:focus:bg-[#444545]",
      "data-[highlighted]:bg-neutral-100 dark:data-[highlighted]:bg-[#444545]",
      "data-disabled:pointer-events-none data-disabled:opacity-50",
      "data-[state=checked]:bg-neutral-100 dark:data-[state=checked]:bg-[#444545] data-[state=checked]:font-medium",
      "dark:text-white",
      className,
    )}
    {...props}
  >
    <span
      className={cn(
        "absolute left-2 flex h-3.5 w-3.5 items-center justify-center",
        description && "top-2.5",
      )}
    >
      <SelectPrimitive.ItemIndicator>
        <Check className="w-4 h-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <span className="flex min-w-0 flex-col">
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      {description ? (
        <span className="mt-0.5 whitespace-normal font-normal type-body-xs-regular text-neutral-500 dark:text-neutral-300">
          {description}
        </span>
      ) : null}
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

/**
 * Item whose checkmark trails the label instead of leading it, matching
 * `DropdownMenuRadioItemRight`. Use it when rows carry their own leading icon,
 * which a leading checkmark would crowd.
 */
const SelectItemRight = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center justify-between gap-3 rounded-md px-2 py-1.5 type-body-s-regular outline-hidden text-neutral-900",
      "focus:bg-neutral-100 dark:focus:bg-[#444545]",
      "data-[highlighted]:bg-neutral-100 dark:data-[highlighted]:bg-[#444545]",
      "data-disabled:pointer-events-none data-disabled:opacity-50",
      "dark:text-white",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    {/* ItemIndicator only mounts when checked, so unchecked rows reserve no trailing space */}
    <SelectPrimitive.ItemIndicator className="flex h-4 w-4 items-center justify-center">
      <Check className="w-4 h-4" />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));
SelectItemRight.displayName = "SelectItemRight";

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(
      "-mx-1 my-1 h-px bg-neutral-100 dark:bg-[#444545]",
      className,
    )}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectItemRight,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
